<template>
  <div class="Pagination">
    <div
      v-if="rowsPerPage !== undefined"
      class="Pagination-rows"
    >
      <span class="Pagination-rowsLabel">{{ t('ui.global.pagination.rowsPerPage') }}</span>

      <Dropdown
        v-model="rowsPerPage"
        class="Pagination-rowsDropdown"
        :options="rowsOptions"
        :aria-label="t('ui.global.pagination.rowsPerPageLabel')"
      />
    </div>

    <div class="Pagination-pages">
      <IconButton
        icon="chevron-left"
        :size="20"
        :disabled="currentPage <= 1"
        :ariaLabel="t('ui.global.pagination.previousPage')"
        @click="goToPage(currentPage - 1)"
      />

      <div class="Pagination-pageNumbers">
        <template
          v-for="(item, index) of pageItems"
          :key="index"
        >
          <span
            v-if="item === ELLIPSIS"
            class="Pagination-ellipsis"
            aria-hidden="true"
          >...</span>

          <button
            v-else
            :class="['Pagination-page', { 'is-active': item === currentPage }]"
            type="button"
            :aria-label="`${t('ui.global.pagination.page')} ${item}`"
            :aria-current="item === currentPage ? 'page' : undefined"
            @click="goToPage(item)"
          >
            {{ item }}
          </button>
        </template>
      </div>

      <IconButton
        icon="chevron-right"
        :size="20"
        :disabled="currentPage >= totalPages"
        :ariaLabel="t('ui.global.pagination.nextPage')"
        @click="goToPage(currentPage + 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Dropdown from '../Dropdown/Dropdown.vue';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import type { DropdownOption } from '../Dropdown/Dropdown.model';
import { DEFAULT_ROWS_OPTIONS, ELLIPSIS, MAX_VISIBLE_PAGES, NEAR_EDGE_THRESHOLD, NEAR_EDGE_OFFSET, type PaginationProps } from './Pagination.model';

const props = withDefaults(defineProps<PaginationProps>(), {
  rowsPerPageOptions: () => DEFAULT_ROWS_OPTIONS,
});

const currentPage = defineModel<number>('currentPage', { required: true });
const rowsPerPage = defineModel<number>('rowsPerPage');

const { t } = useI18n();

const rowsOptions = computed<DropdownOption<number>[]>(() => props.rowsPerPageOptions.map((num) => ({ label: String(num), value: num })));

const pageItems = computed<Array<number | typeof ELLIPSIS>>(() => {
  const total = props.totalPages;
  const current = currentPage.value;

  if (total <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  if (current <= NEAR_EDGE_THRESHOLD) {
    return [1, NEAR_EDGE_OFFSET, NEAR_EDGE_THRESHOLD, ELLIPSIS, total];
  }

  if (current >= total - NEAR_EDGE_OFFSET) {
    return [1, ELLIPSIS, total - NEAR_EDGE_OFFSET, total - 1, total];
  }

  return [1, ELLIPSIS, current - 1, current, current + 1, ELLIPSIS, total];
});

function goToPage(page: number): void {
  if (page < 1 || page > props.totalPages || page === currentPage.value) {
    return;
  }

  currentPage.value = page;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .Pagination {
    display: flex;
    align-items: flex-end;
    gap: vars.$dimensions-d-8;

    @include responsive.mobile {
      flex-direction: column;
      align-items: center;
      gap: vars.$dimensions-d-4;
    }

    &-rows {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-3;
      flex-shrink: 0;
    }

    &-rowsLabel {
      color: vars.$molecules-pagination-text-color;
      font-size: vars.$font-size-md;
      font-weight: vars.$typography-weight-body;
      letter-spacing: -0.03em;
      white-space: nowrap;

      @include responsive.mobile {
        font-size: vars.$font-size-sm;
      }
    }

    &-rowsDropdown {
      width: 64px;

      .Dropdown-field {
        height: 29px;
        padding-inline: vars.$atoms-dropdown-baseinput-padding-left-right;
        padding-block: vars.$atoms-dropdown-baseinput-padding-top-bottom;
      }
    }

    &-pages {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    &-pageNumbers {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
    }

    &-page {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: vars.$dimensions-d-10;
      height: 29px;
      padding-inline: vars.$dimensions-d-2;
      background-color: vars.$atoms-dropdown-baseinput-filled-bg-color;
      border: vars.$atoms-dropdown-baseinput-border-stroke solid vars.$atoms-dropdown-baseinput-filled-border-color;
      border-radius: vars.$border-radius-xs;
      color: vars.$atoms-dropdown-baseinput-text-color;
      font-size: vars.$font-size-sm;
      font-weight: vars.$typography-weight-body;
      letter-spacing: -0.03em;
      cursor: pointer;
      transition: border-color 0.15s ease;

      &.is-active {
        border-color: vars.$color-brand-secondary-900;
        color: vars.$color-brand-secondary-900;
        font-weight: vars.$typography-weight-strong;
      }

      &:not(.is-active):hover {
        border-color: vars.$color-neutral-gray-700;
      }

      &:focus-visible {
        outline: vars.$dimensions-d-0-25 solid vars.$color-brand-focus;
        outline-offset: vars.$dimensions-d-0-25;
      }
    }

    &-ellipsis {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: vars.$dimensions-d-10;
      height: 29px;
      color: vars.$molecules-pagination-text-color;
      font-size: vars.$font-size-md;
      letter-spacing: -0.03em;
      user-select: none;

      @include responsive.mobile {
        font-size: vars.$font-size-sm;
      }
    }
  }
}
</style>
